import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppButton,
  Box,
  FormProvider,
  Inscription,
  NavigationLeftIcon,
  Preloader,
  RawButton,
  Text,
  useResponsive,
} from '@common-fe/common-fe';

import { Modal } from '@/components';

import { AuthorizedUser, CreateAuthorizedUserParams, UpdateAuthorizedUserParams } from '../../authorizedUsers.types';

import useAuthorizedUserForm, { AuthorizedUserFormValues } from './hooks/useAuthorizedUserForm';
import AuthorizedUsersForm from './AuthorizedUsersForm';

interface Props {
  editingUser: AuthorizedUser | null;
  isSubmitting: boolean;
  onCreate: (params: CreateAuthorizedUserParams) => void;
  onUpdate: (params: UpdateAuthorizedUserParams) => void;
  onClose: () => void;
}

const AuthorizedUsersModal = ({ editingUser, isSubmitting, onCreate, onUpdate, onClose }: Props) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const isEditMode = Boolean(editingUser);
  const { methods } = useAuthorizedUserForm({ defaultValue: editingUser });

  const handleSubmit = (values: AuthorizedUserFormValues) => {
    const params = {
      firstName: values.firstName,
      lastName: values.lastName,
      relationshipType: values.relationshipType as AuthorizedUser['relationshipType'],
      birthOn: values.birthOn,
    };
    if (isEditMode) {
      onUpdate(params);
    } else {
      onCreate(params);
    }
  };

  const title = isEditMode ? t('Edit Authorized User') : t('Add Authorized User');
  const description = t('Identify additional individuals who are allowed to be provided with account information when contacting call center.');

  const submitButton = (
    <AppButton
      type="button"
      fill={isMobile}
      width={isMobile ? undefined : 'control'}
      testId="authorized-users-modal_submit"
      onClick={methods.handleSubmit(handleSubmit)}
    >
      {isSubmitting ? (
        <Preloader testId="authorized-users-modal_submit-preloader" color="white" />
      ) : isEditMode ? (
        t('Save')
      ) : (
        t('Add')
      )}
    </AppButton>
  );

  const formContent = (
    <FormProvider {...methods}>
      <AuthorizedUsersForm />
    </FormProvider>
  );

  return (
    <Modal
      visible
      isFullScreen={isMobile}
      isCloseButtonHidden={isMobile}
      contentMobileStyle={{ padding: 0 }}
      onSetVisible={onClose}
      testId="authorized-users-modal"
    >
      {isMobile ? (
        <>
          <Box pad={{ top: 'spacing24', right: 'spacing8', left: 'spacing10' }}>
            <RawButton onClick={onClose} data-testid="authorized-users-modal_back-button">
              <Box direction="row" align="center">
                <Box margin={{ left: 'spacing10' }}>
                  <NavigationLeftIcon color="textAccent" size="20px" />
                </Box>
                <Text color="textAccent" size="small" weight={700} margin={{ left: 'spacing20' }}>
                  {title}
                </Text>
              </Box>
            </RawButton>
          </Box>

          <Box pad={{ top: 'spacing12' }}>
            <Inscription size="medium" color="textBody" margin={{ bottom: 'spacing12', horizontal: 'spacing16' }}>
              {description}
            </Inscription>
            <Box background="module" round="container3Round" pad="spacing16" margin={{ horizontal: 'spacing2' }}>
              <Box
                round="container1Round"
                border={{ color: 'border2', size: '1px' }}
                background="canvas"
                pad="spacing16"
              >
                {formContent}
              </Box>
            </Box>
          </Box>

          <Box direction="row" gap="spacing12" pad={{ vertical: 'spacing32', horizontal: 'spacing24' }} margin={{ top: 'auto' }}>
            <Box flex="grow">
              <AppButton
                type="button"
                buttonType="secondary"
                fill
                testId="authorized-users-modal_cancel"
                onClick={onClose}
              >
                {t('Cancel')}
              </AppButton>
            </Box>
            <Box flex="grow">
              {submitButton}
            </Box>
          </Box>
        </>
      ) : (
        <>
          <Box
            direction="row"
            align="center"
            justify="center"
            pad={{ bottom: 'spacing12', horizontal: 'spacing24' }}
          >
            <Text
              size="2xl"
              color="textBody"
              weight="bold"
              textAlign="center"
              style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {title}
            </Text>
          </Box>

          <Box pad={{ horizontal: 'spacing24' }}>
            <Inscription size="medium" color="textBody" textAlign="center" margin={{ bottom: 'spacing12' }}>
              {description}
            </Inscription>
            <Box round="container1Round" background="module" pad="spacing24">
              <Box
                round="container1Round"
                border={{ color: 'border2', size: '1px' }}
                background="canvas"
                pad="spacing24"
              >
                {formContent}
              </Box>
            </Box>

            <Box direction="row" justify="end" gap="spacing12" pad={{ top: 'spacing16' }}>
              <AppButton
                type="button"
                buttonType="secondary"
                width="control"
                testId="authorized-users-modal_cancel"
                onClick={onClose}
              >
                {t('Cancel')}
              </AppButton>
              {submitButton}
            </Box>
          </Box>
        </>
      )}
    </Modal>
  );
};

export default AuthorizedUsersModal;
